import Socket from './socket'

class WorkStationHub extends Socket {
  constructor(rootStore) {
    super('workStation', rootStore)
    this.rootStore = rootStore
  }

  configureHub() {
    super.configureHub()

    const debugId = this.connection.__debugId
    const withLog = (eventName, handler) => (...args) => {
      console.info(`${this.logTag} event received: ${eventName}`, { debugId, args })
      const fn = handler || (() => {})
      return fn(...args)
    }

    this.connection.on('EmployeeLoggedOut', withLog('EmployeeLoggedOut', this.employeeLoggedOutHandler))
    this.connection.on('EmployeeLoggedIn', withLog('EmployeeLoggedIn', this.employeeLoggedInHandler))
    this.connection.on(
      'EmployeeChangedStatus',
      withLog('EmployeeChangedStatus', this.employeeChangedStatusHandler)
    )
    this.connection.on(
      'ProductionAlertOpened',
      withLog('ProductionAlertOpened', this.productionAlertOpenedHandler)
    )
    this.connection.on(
      'ProductionAlertAcknowledged',
      withLog('ProductionAlertAcknowledged', this.productionAlertAcknowledgedHandler)
    )
    this.connection.on(
      'ProductionAlertClosed',
      withLog('ProductionAlertClosed', this.productionAlertClosedHandler)
    )
    this.connection.on(
      'WorkStationOrderStarted',
      withLog('WorkStationOrderStarted', this.workStationOrderStartedHandler)
    )
    this.connection.on(
      'WorkStationOrderPaused',
      withLog('WorkStationOrderPaused', this.workStationOrderPausedHandler)
    )
    this.connection.on(
      'WorkStationOrderResumed',
      withLog('WorkStationOrderResumed', this.workStationOrderResumedHandler)
    )
    this.connection.on(
      'WorkStationOrderPauseChanged',
      withLog('WorkStationOrderPauseChanged', this.workStationOrderPauseChangedHandler)
    )
    this.connection.on(
      'WorkStationOrderFinished',
      withLog('WorkStationOrderFinished', this.workStationOrderFinishedHandler)
    )
    this.connection.on(
      'RealLaborTimeUpdated',
      withLog('RealLaborTimeUpdated', this.realLaborTimeUpdatedHandler)
    )
    this.connection.on(
      'ProducedQuantitiesUpdated',
      withLog('ProducedQuantitiesUpdated', this.producedQuantitiesUpdatedHandler)
    )
    this.connection.on('ScheduleUpdated', withLog('ScheduleUpdated', this.scheduleUpdatedHandler))
    this.connection.on('WorkOrderChanged', withLog('WorkOrderChanged', this.workOrderChangedHandler))
    this.connection.on('WorkOrderReset', withLog('WorkOrderReset', this.workOrderResetHandler))
    this.connection.on(
      'MachineIntegrationUpdated',
      withLog('MachineIntegrationUpdated', this.machineIntegrationUpdated)
    )

    this.connection.on(
      'WorkOrderNoteUpdated',
      withLog(
        'WorkOrderNoteUpdated',
        this.workOrderNoteUpdateHandler ||
          ((e) => {
            console.debug('Work order note updated', e)
          })
      )
    )

    this.connection.on(
      'ProcessUpdatedOnWorkStation',
      withLog('ProcessUpdatedOnWorkStation', this.processUpdatedHandler)
    )

    this.connection.on(
      'ProcessExecutionUpdated',
      withLog('ProcessExecutionUpdated', this.processExecutionUpdated)
    )

    this.connection.on('WorkStationPaused', withLog('WorkStationPaused', this.workStationPausedHandler))
    this.connection.on(
      'WorkStationResumed',
      withLog('WorkStationResumed', this.workStationResumedHandler)
    )
  }

  joinWorkStation = async (workStationId) => {
    console.debug(this.logTag, 'joinWorkStation() called', {
      workStationId,
      hasConnection: !!this.connection,
    })
    if (!this.connection) return
    try {
      const result = await this.invoke('joinWorkStation', workStationId)
      console.debug(this.logTag, 'joinWorkStation() succeeded', { workStationId, result })
      return result
    } catch (error) {
      console.error(this.logTag, 'joinWorkStation() failed', { workStationId, error })
      throw error
    }
  }

  joinGeneral = async () => {
    console.debug(this.logTag, 'joinGeneral() called', { hasConnection: !!this.connection })
    if (!this.connection) return
    try {
      const result = await this.invoke('JoinGeneral')
      console.debug(this.logTag, 'joinGeneral() succeeded', { result })
      return result
    } catch (error) {
      console.error(this.logTag, 'joinGeneral() failed', { error })
      throw error
    }
  }

  loginOrLogoutEmployee = async (employeeId) =>
    await this.invoke('LoginOrLogoutEmployee', employeeId)

  loginOrLogoutEmployeeWithWorkOrder = async (employeeId, workOrderId) => {
    if (!this.connection) {
      await this.connect()
    }
    try {
      // Use -1 to indicate "no work order selected" (null causes issues with SignalR serialization)
      const woId = workOrderId === undefined || workOrderId === null ? -1 : workOrderId
      return await this.invoke('LoginOrLogoutEmployeeWithWorkOrder', employeeId, woId)
    } catch (error) {
      console.error('Error in loginOrLogoutEmployeeWithWorkOrder:', error)
      throw error
    }
  }

  acknowledgeProductionAlert = async (id, acknowledgedEmployeeId) =>
    await this.invoke('AcknowledgeProductionAlert', id)

  closeProductionAlert = async (id, closedEmployeeId, alertReason) => {
    await this.invoke('CloseProductionAlert', id, alertReason || 'Não informado')
  }

  startWorkStationOrder = async (id, type, beginEmployeeId) =>
    await this.invoke('StartWorkStationOrder', {
      id,
      type,
      beginEmployeeId,
    })

  resumeWorkStationOrder = async (id, type, resumeEmployeeId) =>
    await this.invoke('ResumeWorkStationOrder', {
      id,
      type,
      resumeEmployeeId,
    })

  changeCurrentWorkOrder = async ({
    currentWorkOrderId,
    workOrderId,
    assignedWorkStationId,
    note,
    loggedEmployeeIds,
    isSetup,
  }) =>
    await this.invoke('ChangeCurrentWorkOrder', {
      currentWorkOrderId,
      workOrderId,
      assignedWorkStationId,
      note,
      loggedEmployeeIds,
      isSetup,
    })

  pauseWorkStationOrder = async (id, type, pauseEmployeeId, currentStatusId, note, parentId) =>
    await this.invoke('PauseWorkStationOrder', {
      id,
      type,
      pauseEmployeeId,
      currentStatusId,
      note,
      parentStatusId: parentId,
    })

  changeWorkStationOrderPause = async (
    workOrderId,
    runtimeId,
    pauseStatusId,
    note,
    parentStatusId,
    employeeId
  ) =>
    await this.invoke('ChangeWorkStationOrderPause', {
      workOrderId,
      workOrderRuntimeId: runtimeId,
      pauseStatusId,
      note,
      parentStatusId,
      employeeId,
    })

  pauseWorkStation = async (id, type, pauseEmployeeId, currentStatusId, note, workStationId) =>
    await this.invoke('PauseWorkStation', {
      id,
      type,
      pauseEmployeeId,
      currentStatusId,
      note,
      workStationId,
    })

  resumeWorkStation = async (id, type, pauseEmployeeId, currentStatusId, note, workStationId) =>
    await this.invoke('ResumeWorkStation', {
      id,
      type,
      pauseEmployeeId,
      currentStatusId,
      note,
      workStationId,
    })

  // scrapReasons: [{ scrapReasonId, quantity }] com a distribuição do refugo total da ordem.
  // undefined = não informado (o backend mantém a distribuição atual).
  finishWorkStationOrder = async (
    id,
    type,
    endEmployeeId,
    producedQuantity,
    defectiveQuantity,
    scrapReasons
  ) =>
    await this.invoke('FinishWorkStationOrder', {
      id,
      type,
      endEmployeeId,
      producedQuantity,
      defectiveQuantity,
      scrapReasons,
    })

  updateProducedQuantities = async (
    workOrderId,
    producedQuantity,
    defectiveQuantity,
    scrapReasons
  ) => {
    await this.invoke('UpdateProducedQuantities', {
      workOrderId,
      producedQuantity,
      defectiveQuantity,
      scrapReasons,
    })
  }

  openProductionAlert = async (openedEmployeeId, typeId, workOrderId = null) =>
    await this.invoke(
      'OpenProductionAlert',
      openedEmployeeId ? openedEmployeeId : '',
      typeId,
      workOrderId ?? 0
    )

  setEmployeeStoppage = async (employeeId, stoppageId, note) =>
    await this.invoke('SetEmployeeStoppage', employeeId, stoppageId, note)

  clearEmployeeStoppage = async (employeeId) =>
    await this.invoke('ClearEmployeeStoppage', employeeId)

  resetWorkOrder = async (workOrderId) => {
    await this.invoke('ResetWorkOrder', workOrderId)
  }

    schedule = async (ordersSchedule) => {
      if (!this.connection || typeof this.connection.invoke !== 'function' || !this.isConnected) {
        await this.connect()
      }

      if (!this.connection || typeof this.connection.invoke !== 'function' || !this.isConnected) {
        throw new Error('Conexao do planejamento indisponivel. Tente novamente em alguns segundos.')
      }

      return await this.invoke('Schedule', ordersSchedule)
    }

  shiftAndSchedule = async (ordersSchedule) => {
    await this.invoke('ShiftAndSchedule', ordersSchedule)
  }

  validateConnection = async () => {
    if (!this.isConnected) await this.connect()
  }

  /* Override functions */
  employeeLoggedOutHandler
  onEmployeeLoggedOut = (handler) => (this.employeeLoggedOutHandler = handler)

  employeeLoggedInHandler
  onEmployeeLoggedIn = (handler) => (this.employeeLoggedInHandler = handler)

  employeeChangedStatusHandler
  onEmployeeChangedStatus = (handler) => (this.employeeChangedStatusHandler = handler)

  productionAlertOpenedHandler
  onProductionAlertOpened = (handler) => (this.productionAlertOpenedHandler = handler)

  productionAlertAcknowledgedHandler
  onProductionAlertAcknowledged = (handler) => (this.productionAlertAcknowledgedHandler = handler)

  productionAlertClosedHandler
  onProductionAlertClosed = (handler) => (this.productionAlertClosedHandler = handler)

  workStationOrderStartedHandler
  onWorkStationOrderStarted = (handler) => (this.workStationOrderStartedHandler = handler)

  workStationOrderPausedHandler
  onWorkStationOrderPaused = (handler) => (this.workStationOrderPausedHandler = handler)

  workStationOrderPauseChangedHandler
  onWorkStationOrderPauseChanged = (handler) =>
    (this.workStationOrderPauseChangedHandler = handler)

  workStationOrderResumedHandler
  onWorkStationOrderResumed = (handler) => (this.workStationOrderResumedHandler = handler)

  workStationOrderFinishedHandler
  onWorkStationOrderFinished = (handler) => (this.workStationOrderFinishedHandler = handler)

  realLaborTimeUpdatedHandler
  onRealLaborTimeUpdated = (handler) => (this.realLaborTimeUpdatedHandler = handler)

  producedQuantitiesUpdatedHandler
  onProducedQuantitiesUpdated = (handler) => (this.producedQuantitiesUpdatedHandler = handler)

  scheduleUpdatedHandler
  onScheduleUpdated = (handler) => (this.scheduleUpdatedHandler = handler)

  workOrderChangedHandler
  onWorkOrderChanged = (handler) => (this.workOrderChangedHandler = handler)

  workOrderResetHandler
  onWorkOrderReset = (handler) => (this.workOrderResetHandler = handler)

  machineIntegrationUpdated
  onMachineIntegrationUpdated = (handler) => (this.machineIntegrationUpdated = handler)

  processExecutionUpdated
  onProcessExecutionUpdated = (handler) => (this.processExecutionUpdated = handler)

  workOrderNoteUpdateHandler
  onWorkOrderNoteUpdate = (handler) => (this.workOrderNoteUpdateHandler = handler)

  processUpdatedHandler
  onProcessUpdate = (handler) => (this.processUpdatedHandler = handler)

  workStationPausedHandler
  onWorkStationPaused = (handler) => (this.workStationPausedHandler = handler)

  workStationResumedHandler
  onWorkStationResumed = (handler) => (this.workStationResumedHandler = handler)

  // processExecutionUpdatedHandler
  // onProcessExecutionUpdated = (handler) => (this.processExecutionUpdatedHandler = handler)
}

export default WorkStationHub
